import { Injectable } from '@angular/core';
import { RequestWrapperClass } from 'src/app/shared/services/request-wrapper.class';
import { UserStateService } from 'src/app/shared/services/user-state.class';
import { AppSetting, AppSettingsGQL } from '../generated/schema';
import { from, map, Observable, shareReplay, switchMap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { versionComparator } from '../utils/version-comparator';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';

const MIN_VALID_APP_VERSION = 'minSupportedAppVersion';
const APP_STORE_LINK = 'storeLinkIos';
const PLAY_MARKET_LINK = 'storeLinkAndroid';

@Injectable({
  providedIn: 'root'
})
export class AppSettingsService extends RequestWrapperClass {

  private appSettings$: Observable<AppSetting[]>;

  constructor(
    private appSettingsService: AppSettingsGQL,
    public userStateService: UserStateService,
  ) {
    super(userStateService);
    this.appSettings$ = this.getAppSettings().pipe(shareReplay(1));
  }

  get appSettings(): Observable<AppSetting[]> {
    return this.appSettings$;
  }

  getAppSettings(): Observable<AppSetting[]> {
    return this.createRetryableObserver<AppSetting[]>(async () => {
      const response = await this.appSettingsService.fetch().toPromise();
      return <AppSetting[]>response?.data?.appSettings;
    });
  }

  checkIsUpdateRequired(): Observable<boolean> {
    return this.appSettings.pipe(
      switchMap((settings: AppSetting[]) =>
        from(this.getCurrentAppVersion()).pipe(
          map((currentVersion: string) => {
            const minVersion = settings.find((item: AppSetting) => item.id === MIN_VALID_APP_VERSION);
            const requiredVersion = minVersion?.value;
            const comparisonResult = versionComparator(currentVersion, requiredVersion);
            return comparisonResult < 0;
          })
        )
      )
    )
  }

  // Native binary version is the source of truth for the update gate; the App
  // Store is what the user actually updates. environment.appVersion is only a
  // build-time constant (see scripts/sync-version.js) used as the web fallback.
  private async getCurrentAppVersion(): Promise<string> {
    if (Capacitor.isNativePlatform()) {
      const info = await App.getInfo();
      return info.version;
    }
    return environment.appVersion;
  }

  getStoreLink(type: 'ios' | 'android' | 'web'): Observable<string> {
    return this.appSettings.pipe(
      map((settings: AppSetting[]) => {
        switch (type) {
          case 'ios':
            return settings.find((item: AppSetting) => item.id === APP_STORE_LINK)?.value || '';
          case 'android':
            return settings.find((item: AppSetting) => item.id === PLAY_MARKET_LINK)?.value || '';
          default:
            return 'web'
        }
      })
    )
  }
}
